import React, { useRef, useState, useMemo } from "react";
import banniere_groupe from "assets/banniere_groupe.webp";
import "./GroupeDetails.scss";
import "primeicons/primeicons.css";
import GroupePublication from "../GroupePublication/GroupePublication";
import { Menu } from "primereact/menu";
import BtnBlanc from "Components/Boutons/BtnBlanc/btn-blanc";
import { FaEllipsisH } from "react-icons/fa";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { updateGroups } from "Redux/Actions/groupsActions";
import {
  formatDate,
  formatDateLocale,
  isNotEmptyArray,
} from "Services/functions";
import GroupeCardEvent from "../GroupeCard/GroupeCardEvent/GroupeCardEvent";
import { DateTime } from "luxon";
import { Link, useParams } from "react-router-dom";
import { fetchGet, fetchPost } from "Services/api";
import parse from "html-react-parser";
import { HTMLParseOptions } from "Services/htmlParseOptions";
import { useTranslation } from "react-i18next";
import Loader from "Components/Loader/loader";

/**
 * Fonction pour trier les members dans l'ordre suivant :
 * 1. rôles : CREATEUR > ADMIN > USER
 * 2. nom de famille
 * 3. prénom
 * @param {{firstname: String, surname: String, isGroupAdmin: boolean, isGroupCreator: boolean}} firstMember - premier membre à comparer
 * @param {{firstname: String, surname: String, isGroupAdmin: boolean, isGroupCreator: boolean}} secondMember - second membre à comparer
 */
const sortMembers = (firstMember, secondMember) => {
  // 1. rôles : CREATEUR > ADMIN > USER
  // 1.1. CREATEUR
  if (firstMember.isGroupCreator && !secondMember.isGroupCreator) return -1;
  if (!firstMember.isGroupCreator && secondMember.isGroupCreator) return 1;
  // 1.2. ADMIN
  if (firstMember.isGroupAdmin && !secondMember.isGroupAdmin) return -1;
  if (!firstMember.isGroupAdmin && secondMember.isGroupAdmin) return 1;
  // 2. nom de famille
  if (firstMember.surname > secondMember.surname) return 1;
  if (firstMember.surname < secondMember.surname) return -1;
  // 3. prénom
  if (firstMember.firstname > secondMember.firstname) return 1;
  if (firstMember.firstname < secondMember.firstname) return -1;
  // Si tout est égal (cas improbable) on retourne 0
  return 0;
};

/**
 * Permet d'afficher le détail d'un groupe lorsque l'on a cliqué dessus dans la page mes groupes d'échanges
 */
const GroupeDetails = (props) => {
  const { t } = useTranslation("common");
  const menu = useRef(null);
  let { id } = useParams();
  const menuGroupe = useRef(null);

  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [members, setMembers] = useState([]);

  const [currentGroup, setCurrentGroup] = useState(undefined);
  const [groupEvents, setGroupEvents] = useState([]);

  // -----------  Désactivation du groupe

  const handleDeactivateGroup = async () => {
    const response = await fetchPost(
        "/groups/" + currentGroup.id + "/disable",
        "{}",
        props.auth.token
    );
    let isError = false;
    if (!response?.id || response?.id != currentGroup.id) isError = true;
    props.setDeactivateGroup({
      visible: true,
      type: isError ? "error" : "success",
      detail: isError
          ? "Une erreur est survenue lors de la désactivation du groupe"
          : "Le groupe a bien été désactivé",
      header: "Désactivation du groupe",
    });
  };

  // ---------- GESTION DES MEMBRES DU GROUPE
  const handleGroupMembers = async () => {
    if (currentGroup != null) {
      // Tout d'abord on récupère le statut ACTIF depuis la liste des statuts de redux
      const activeStatus = props.states.groupMemberStatesData.find(
          (status) => status.label == "ACTIF"
      );
      // On récupère ensuite la liste des membres du groupe
      // dont le statut == ACTIF
      const groupMembers = await fetchGet("/group_members", props.auth.token, {
        groupSource: currentGroup?.id,
        groupMemberStatus: activeStatus?.id,
      });
      let membersList = [];
      groupMembers.map((member) => {
        const isGroupAdmin = member.groupMemberRoles.some(
            (groupMember) => groupMember.role.roleName == "ROLE_GROUP_ADMIN"
        );
        const isGroupCreator = member.groupMemberRoles.some(
            (groupMember) => groupMember.role.roleName == "ROLE_GROUP_CREATEUR"
        );
        membersList.push({
          isGroupAdmin,
          isGroupCreator,
          surname: member.user.surname,
          firstname: member.user.firstname,
          label:
              member.user.surname.toUpperCase() + " " + member.user.firstname,
          icon: isGroupAdmin ? "pi pi-shield" : "pi pi-user",
        });
      });
      membersList = membersList.sort((a, b) => sortMembers(a, b, membersList));
      setMembers(membersList);
    }
  };

  // Récupération des membres du groupe
  useMemo(async () => {
    handleGroupMembers();
  }, [currentGroup, props.items.selectedGroup]);

  // ---------------------------------------

  // ---------- GESTION DU ROLE DU MEMBRE CONNECTÉ
  const handleUserAdmin = async () => {
    let adminMember =
        members.find(
            (member) =>
                member.firstname == props.auth.userConnected.firstname &&
                member.surname == props.auth.userConnected.surname &&
                member.isGroupAdmin == true
        ) || null;
    const isAdmin = adminMember != null ? true : false;

    return isAdmin;
  };

  // Vérification si l'utilisateur est admin du groupe
  useMemo(async () => {
    let isGroupAdmin = null;

    if (currentGroup != null && props.auth.userConnected != null) {
      isGroupAdmin =
          props.auth.isAdmin === true ? true : await handleUserAdmin();
      setIsGroupAdmin(isGroupAdmin);
    }
  }, [currentGroup, props.auth.isAdmin, members]);

  // ----------------------------------------------

  // Menu trois petits point dans image couverture en fonction du rôle de l'utilisateur
  const itemGroupeUser = [{}];
  const itemGroupeAdmin = [
    // { label: "Partager" },
    // { separator: true },
    {
      label: t("groupManagement.disable"),
      command: () => {
        handleDeactivateGroup();
      },
    },
  ];

  useMemo(async () => {
    if (id) setCurrentGroup(await fetchGet("/groups/" + id, props.auth.token));
    else if (props.groupId != null)
      setCurrentGroup(
          await fetchGet("/groups/" + props.groupId, props.auth.token)
      );
  }, [props.groupId, id]);

  // on récupère les événements du groupe
  useMemo(async () => {
    if (currentGroup) {
      let groupEvents = [];
      groupEvents = await fetchGet("/event_groups", props.auth.token, {
        associatedGroup: currentGroup.id,
        "startingAt[after]": DateTime.local().toISO(),
      });
      setGroupEvents(groupEvents);
    }
  }, [currentGroup]);

  return (
      <div className="groupe_details_container">
        {props.dataLoaded && currentGroup && (
            <>
              <div className="groupe_details_img">
                {!props.finder && (props.component === null) &&(
                    <div className="bouton_groupe">
                      <BtnBlanc
                          btnAction={(event) => menuGroupe.current.toggle(event)}
                      >
                        <FaEllipsisH />
                      </BtnBlanc>
                      {/* Si le composant est affiché depuis la page de recherche, on
              n'affiche pas le menu */}
                      <Menu
                          appendTo={document.getElementById("app")}
                          model={isGroupAdmin ? itemGroupeAdmin : itemGroupeUser}
                          popup
                          ref={menuGroupe}
                          style={{ textAlign: "center", width: "fit-content" }}
                      />
                    </div>
                )}

                <img
                    src={
                      currentGroup?.imageUrl
                          ? `${currentGroup.imageUrl}`
                          : banniere_groupe
                    }
                    alt="Bannière du groupe"
                    title="Bannière du groupe"
                />
              </div>
              <div className="groupe_details_header">
                <div className="groupe_details_header__title">
                  <h2>{currentGroup.label}</h2>
                  {/* Si l'user n'est pas admin du portail, on affiche le bouton rejoindre ou quitter le groupe */}
                  {!props.auth.isAdmin && (
                      <>
                        {/* Cas où le membre est dans le groupe */}
                        {!props.finder && (props.component === null) && (
                            <button
                                className="leave"
                                onClick={() =>
                                    props.setLeaveGroup({
                                      id: currentGroup.id,
                                      state: true,
                                      title: currentGroup.label,
                                    })
                                }
                            >
                              Quitter le groupe
                            </button>
                        )}

                        {/* Cas où le membre n'est pas dans le groupe */}
                        {props.finder && (props.component === null) && (
                            <button
                                className="leave"
                                onClick={() =>
                                    props.setJoinVisible({
                                      title: currentGroup.label,
                                      state: true,
                                      id: currentGroup.id,
                                      private: currentGroup.private,
                                    })
                                }
                            >
                              Rejoindre le groupe
                            </button>
                        )}
                      </>
                  )}
                </div>

                <div className="infos">
                  <p>
                    {currentGroup?.public ? (
                        <React.Fragment>
                          <i className="pi pi-lock-open icon_infos"></i>
                          {t("createGroup.public")}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                          <i className="pi pi-lock icon_infos"></i>
                          {t("createGroup.private")}
                        </React.Fragment>
                    )}
                  </p>
                  <Menu
                      appendTo={document.getElementById("app")}
                      model={members}
                      popup
                      ref={menu}
                      style={{ textAlign: "center", width: "fit-content" }}
                  />
                  <p
                      onClick={(event) => menu.current.toggle(event)}
                      className="click"
                  >
                    <i className="pi pi-users icon_infos"></i>
                    {members.length > 1
                        ? members.length + " " + t("groupManagement.members")
                        : members.length + " " + t("groupManagement.member")}
                  </p>
                  <p>
                    {props.i18n.language === "en"
                        ? "Created on " +
                        formatDateLocale(
                            currentGroup.createdAt,
                            props.i18n.language
                        )
                        : "Créé le " +
                        formatDateLocale(
                            currentGroup.createdAt,
                            props.i18n.language
                        )}
                  </p>
                </div>
              </div>
              <div className="groupe_details_description">
                <h5>Description</h5>
                <div>{parse(currentGroup.description || "", HTMLParseOptions)}</div>
                {isNotEmptyArray(groupEvents) && (
                    <>
                      <h6>Prochain événement de mon groupe</h6>
                      <Link
                          to={"/group_event-detail/" + groupEvents[0].id}
                          key={groupEvents[0]}
                      >
                        <GroupeCardEvent
                            image={groupEvents[0].imageUrl ? "link" : "img1"}
                            link={groupEvents[0].imageUrl}
                            titre={groupEvents[0].title}
                            dateDebut={formatDate(groupEvents[0].startingAt, ".")}
                            dateFin={formatDate(groupEvents[0].endingAt, ".")}
                            participants={groupEvents[0].maxNumberOfParticipants}
                        />
                      </Link>
                    </>
                )}
                {currentGroup && currentGroup.id ? (
                    <>
                      <GroupePublication
                          currentGroupId={currentGroup.id}
                          finder={props.finder}
                      />
                    </>
                ) : (
                    <Loader />
                )}
              </div>
            </>
        )}
      </div>
  );
};

GroupeDetails.propTypes = {
  title: PropTypes.string,
  groups: PropTypes.object,
  auth: PropTypes.object,
  handleUpdateGroups: PropTypes.func,
  groupId: PropTypes.number,
  users: PropTypes.object,
  items: PropTypes.object,
  setLeaveGroup: PropTypes.func,
  finder: PropTypes.bool,
  setJoinVisible: PropTypes.func,
  setDeactivateGroup: PropTypes.func,
  states: PropTypes.any,
  i18n: PropTypes.object,
  component: PropTypes.string,
  dataLoaded: PropTypes.bool,
};

GroupeDetails.defaultProps = {
  dataLoaded: false,
};

const mapStateToProps = (state) => ({
  items: state.items,
  auth: state.auth,
  users: state.users,
  groups: state.groups,
  states: state.states,
  i18n: state.i18n,
});

const mapDispatchToProps = (dispatch) => ({
  handleUpdateGroups: (value) => {
    dispatch(updateGroups(value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GroupeDetails);
